import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useEffect, useMemo, useState } from 'react';
import { fieldFromName, toElasticQuery } from '@alliance-disposal/wql';
import axios from 'axios';
import { parse, stringify } from 'qs';
import { FormProvider, useForm } from 'react-hook-form';
import { countFilterFields, filterUnusedBooleanFields, findObjectsWithField } from '../helpers/functions';
///////////////////////////////////////////////
// SOUR SEARCH AND FILTER WRAPPER CUSTOM CONTEXT
///////////////////////////////////////////////
export const CustomFormContext = createContext({
    results: undefined,
    searchActive: false,
    filterActive: false,
    formInitialized: false,
    setSearchActive: () => { },
    setFilterActive: () => { },
    defaultFilters: undefined,
    application: undefined,
    isLoading: false,
    filtersOpen: false,
    highlightActive: false,
    handleResetDefaultFilters: () => { },
    onResetValues: () => { },
    resetValues: null,
    setFiltersOpen: () => { },
    makeParams: () => '',
    onSubmit: (data) => {
        console.log('---------------------------------');
        console.log('SUBMIT PLACEHOLDER', data);
        console.log('---------------------------------');
    },
    onNavigate(path) {
        console.log('---------------------------------');
        console.log('NAVIGATE PLACEHOLDER', path);
        console.log('---------------------------------');
    },
});
///////////////////////////////////////////////
// SOUR SEARCH AND FILTER WRAPPER COMPONENT
///////////////////////////////////////////////
export const SourSearchWrapper = ({ children, defaultFilters, onSubmit: externalSubmit, onNavigate: externalNavigate, onResults, onSearch, onFilter, restrictTo, page, size, options, highlight, createQueryParams, }) => {
    // CUSTOM FORM CONTEXT
    const [application, setApplication] = useState(options.application);
    const [apiKey, setApiKey] = useState(options.apiKey);
    const [environment, setEnvironment] = useState(options.environment);
    const [restriction, setRestriction] = useState(undefined);
    const [results, setResults] = useState();
    const [isLoading, setIsLoading] = useState(false);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [searchActive, setSearchActive] = useState(false);
    const [filterActive, setFilterActive] = useState(false);
    const [formInitialized, setFormInitialized] = useState(false);
    const [highlightActive, setHighlightActive] = useState(false);
    const [resetValues, setResetValues] = useState(null);
    // lastPage is used to stop duplicate fetches if page is set from SourSearch rather than the parent
    const [lastPage, setLastPage] = useState(undefined);
    const [lastSize, setLastSize] = useState(undefined);
    const [params, setParams] = useState(undefined);
    //FORM CONTEXT
    const methods = useForm({
        shouldUnregister: true,
    });
    ///////////////////////////////////////////////
    // FUNCTIONS
    ///////////////////////////////////////////////
    // CHECKS TO SEE IF QUERY IS SAME AS DEFAULT FILTERS
    const handleCheckIfEqualToDefault = (queryData) => {
        console.log('handleCheckIfEqualToDefault', queryData);
        if (!defaultFilters)
            return false;
        const blankQueryObject = {
            filters: {},
            query: { query: '', restrictToFields: [] },
            sort: { field: undefined, direction: 'DESC' },
            type: '',
        };
        const fullDefaultFilterObject = {
            ...blankQueryObject,
            ...defaultFilters.query,
        };
        if (!fullDefaultFilterObject.query.restrictToFields) {
            fullDefaultFilterObject.query.restrictToFields = [];
        }
        const fullQueryData = {
            ...blankQueryObject,
            ...queryData,
        };
        if (!fullQueryData.query.restrictToFields) {
            fullQueryData.query.restrictToFields = [];
        }
        fullQueryData.filters = filterUnusedBooleanFields(findObjectsWithField(fullQueryData.filters));
        fullDefaultFilterObject.filters = filterUnusedBooleanFields(findObjectsWithField(fullDefaultFilterObject.filters));
        return JSON.stringify(fullQueryData) === JSON.stringify(fullDefaultFilterObject);
    };
    // HANDLES ENCODING QUERY INTO STRING
    const handleCreateQueryParams = (queryData, passedPage) => {
        if (queryData === '') {
            window.history.pushState(null, '', `${window.location.pathname}`);
            setParams('');
            return;
        }
        const filterCount = countFilterFields(queryData);
        if (filterCount.totalCount === 0 && !queryData.query?.query) {
            window.history.pushState(null, '', `${window.location.pathname}`);
            setParams('');
            return;
        }
        if (createQueryParams?.removeOn === 'empty_string_inactive_filter' &&
            !queryData?.query?.query &&
            handleCheckIfEqualToDefault(queryData)) {
            window.history.pushState(null, '', `${window.location.pathname}`);
            setParams('');
            return;
        }
        let newUrl = window.location.pathname;
        let appendToSearchString = '&sour-search=true';
        if (passedPage && passedPage > 0 && !createQueryParams?.stopPageAdd)
            appendToSearchString = appendToSearchString + `&page=${passedPage}`;
        if (createQueryParams?.method === 'with_filter') {
            const queryString = stringify(filterCount.totalCount > 0 ? queryData : '', { encode: false });
            if (queryString) {
                setParams(queryString + appendToSearchString);
                newUrl = `${window.location.pathname}?${queryString}${appendToSearchString}`;
            }
        }
        if (createQueryParams?.method === 'all') {
            const queryString = stringify(queryData, { encode: false });
            if (queryString) {
                setParams(queryString + appendToSearchString);
                newUrl = `${window.location.pathname}?${queryString}${appendToSearchString}`;
            }
        }
        window.history.pushState(null, '', newUrl);
    };
    const makeParams = () => {
        return params || '';
    };
    // RETURNS THE VALUE IN "" OR NULL IF NON EXIST
    const findWordInQuotes = (input) => {
        const matches = input.match(/"([^"]*)"/);
        return matches ? matches[1] : null;
    };
    const handlePrepareQuery = (query) => {
        const search = query?.query || '';
        const searchQuery = {
            query: query?.query || '',
            restrictToFields: query?.restrictToFields || [],
        };
        let multiMatchType = undefined;
        let strippedSearchString = search;
        if (search?.includes(':') && application) {
            const inputArray = [search.slice(0, search.indexOf(':')), search.slice(search.indexOf(':') + 1)];
            const searchableField = inputArray[0];
            const searchValue = inputArray[1];
            const fields = fieldFromName(searchableField, {
                application: application,
            });
            const restrictedFields = new Set();
            fields.map((field) => {
                restrictedFields.add(field.field);
            });
            searchQuery.query = searchValue;
            searchQuery.restrictToFields = Array.from(restrictedFields);
            strippedSearchString = searchValue;
        }
        if (Boolean(findWordInQuotes(strippedSearchString)) && application) {
            multiMatchType = 'phrase_prefix';
            searchQuery.query = strippedSearchString.replace(/"/g, '');
        }
        return { query: searchQuery, multiMatchType: multiMatchType };
    };
    const isNumber = (value) => {
        return (typeof value === 'number' && !isNaN(value)) || value instanceof Number;
    };
    const handleSubmit = async (data, passedPage) => {
        const queryData = { ...data };
        if (application === undefined) {
            return;
        }
        if (environment === undefined) {
            return;
        }
        setIsLoading(true);
        if (filtersOpen) {
            setFiltersOpen(false);
        }
        if (externalSubmit) {
            externalSubmit(queryData);
            return;
        }
        const searchSize = size || 50;
        const searchPage = passedPage || passedPage === 0 ? passedPage : page || 0;
        // CREATE QUERY STRING IF CALLED FOR
        if (createQueryParams) {
            handleCreateQueryParams(queryData, searchPage); // TODO make sure this is ok
        }
        // CHECK TO SEE IF ANY FILTERS OR SEARCH SHOULD BE ACTIVE - TODO can this eliminate any other functions here?
        if (handleCheckIfEqualToDefault(queryData) && !defaultFilters?.activateOnInitial) {
            setFilterActive(false);
            if (!queryData.query?.query)
                setSearchActive(false);
        }
        const parsedQuery = handlePrepareQuery(queryData.query);
        // PREPARE QUERY FOR ELASTIC
        const query = {
            ...queryData,
            filters: filterUnusedBooleanFields(findObjectsWithField(queryData.filters)),
            sort: queryData.sort?.field === undefined ? undefined : queryData.sort,
            query: parsedQuery.query,
        };
        const elasticQuery = toElasticQuery(query, {
            application: application,
            environment: environment,
            haulerID: application === 'wayste' ? restrictTo : undefined,
            customerID: application === 'customer-portal' ? restrictTo : undefined,
            multiMatchType: parsedQuery.multiMatchType,
        });
        const requestPath = Array.isArray(elasticQuery.index) ? elasticQuery.index.join(',') : elasticQuery.index;
        try {
            const esUrl = environment === 'development'
                ? `http://localhost:9200/${requestPath}/_search`
                : `https://sourgum-search.es.us-east-1.aws.found.io/${requestPath}/_search`;
            const searchResults = await axios.post(esUrl, {
                query: elasticQuery.query,
                aggs: elasticQuery.aggs,
                sort: elasticQuery.sort,
            }, {
                headers: {
                    Authorization: `ApiKey ${apiKey}`,
                },
                params: {
                    size: searchSize,
                    from: searchPage ? searchSize * searchPage || 0 : 0,
                },
            });
            const totalHits = isNumber(searchResults.data.hits.total)
                ? searchResults.data.hits.total
                : searchResults.data?.hits?.total?.value || 0;
            const totalPages = Math.ceil(totalHits / searchSize);
            setIsLoading(false);
            return {
                results: searchResults.data,
                page: searchPage,
                size: searchSize,
                totalPages,
                totalHits,
                type: queryData.type,
            };
        }
        catch (error) {
            console.warn('onSubmit ERROR', error);
            setIsLoading(false);
        }
    };
    // PREPARE QUERY FOR ELASTIC && EXPOSES INITIAL RESULTS TO EXTERNAL COMPONENTS
    const onSubmit = async (data, passedPage) => {
        setLastPage(passedPage || 0);
        setLastSize(size || 50);
        const response = await handleSubmit(data, passedPage || 0);
        setResults(response?.results);
        if (response && onResults)
            onResults({ ...response });
    };
    // FETCHES MORE RESULTS BASED ON CURRENT QUERY && EXPOSES PAGINATED > 0 RESULTS TO EXTERNAL COMPONENTS
    const handleGetMoreResults = async () => {
        if (page === undefined || size === undefined || (page === lastPage && size === lastSize)) {
            return;
        }
        setLastPage(page);
        setLastSize(size || 50);
        const data = methods.getValues();
        const response = await handleSubmit(data);
        if (response && onResults) {
            onResults({ ...response });
        }
    };
    // EXPOSE MORE RESULTS ON PAGE OR SIZE CHANGE
    useEffect(() => {
        if (formInitialized)
            handleGetMoreResults();
    }, [page, size]);
    // HANDLES RESETTING FILTER COMPONENTS ON CLICK AWAY
    const onResetValues = (fields) => {
        setResetValues(fields);
        setTimeout(() => {
            setResetValues(null);
        }, 50);
    };
    // HANDLE RESET DEFAULTS CLICK IF THERE ARE DEFAULTS
    const handleResetDefaultFilters = (newQuery) => {
        if (defaultFilters?.activateOnInitial && defaultFilters?.query) {
            onSubmit(defaultFilters.query);
            setFilterActive(true);
            onResetValues('all');
            if (createQueryParams) {
                handleCreateQueryParams(defaultFilters.query);
            }
        }
        else {
            setFilterActive(false);
            setSearchActive(false);
            onResetValues('all');
            if (createQueryParams) {
                handleCreateQueryParams('');
            }
        }
    };
    useEffect(() => {
        methods.setValue('type', defaultFilters?.query?.type || undefined);
        onResetValues('filters');
    }, [defaultFilters]);
    ///////////////////////////////////////////////
    // HOOKS
    ///////////////////////////////////////////////
    //LOGIC TO SET APPLICATION AND ENVIRONMENT FROM OPTIONS
    useEffect(() => {
        if (options.application !== undefined && options.environment !== undefined && options.apiKey !== undefined) {
            setApplication(options.application);
            setApiKey(options.apiKey);
            setEnvironment(options.environment);
        }
    }, [options]);
    const queryParamsDecoder = (value, decoder) => {
        const key = decoder(value);
        const isoDateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?Z$/;
        return isoDateRegex.test(key) ? new Date(key) : key;
    };
    // SETS STARTING VALUES BASED ON QUERY STRING OR DEFAULT FILTERS
    useEffect(() => {
        if (Boolean(createQueryParams) && window.location.search && window.location.search.includes('sour-search=true')) {
            const queryObject = parse(window.location.search.slice(1), {
                decoder: queryParamsDecoder,
            });
            methods.setValue('filters', queryObject.filters);
            methods.setValue('sort', queryObject.sort);
            methods.setValue('type', queryObject.type);
            methods.setValue('query', queryObject.query);
            const passedPage = queryObject.page;
            delete queryObject['sour-search'];
            delete queryObject.page;
            onSubmit(queryObject, Number(passedPage));
            if (!handleCheckIfEqualToDefault(queryObject) || defaultFilters?.activateOnInitial) {
                setFilterActive(true);
                if (queryObject.query?.query)
                    setSearchActive(true);
            }
        }
        else if (defaultFilters) {
            methods.setValue('type', defaultFilters?.query?.type || undefined);
            if (defaultFilters?.query?.filters)
                methods.setValue('filters', defaultFilters.query.filters);
            if (defaultFilters?.query?.sort)
                methods.setValue('sort', defaultFilters.query.sort);
            if (defaultFilters.activateOnInitial && defaultFilters.query) {
                onSubmit(defaultFilters.query);
                setFilterActive(true);
                if (defaultFilters.query?.query)
                    setSearchActive(true);
            }
        }
    }, []);
    // NEEDED TO BE ABLE TO SET STARTING AND DEFAULT FILTER VALUES
    useEffect(() => {
        setTimeout(() => {
            setFormInitialized(true);
        }, 500);
    }, []);
    //EXPOSES SEARCH ACTIVE TO EXTERNAL COMPONENTS
    useEffect(() => {
        if (onSearch) {
            onSearch(searchActive);
        }
    }, [searchActive]);
    //EXPOSES FILTER ACTIVE TO EXTERNAL COMPONENTS
    useEffect(() => {
        if (onFilter) {
            onFilter(filterActive);
        }
    }, [filterActive]);
    //LOGIC TO SET DEFAULT RESTRICTION
    useEffect(() => {
        if (restrictTo) {
            setRestriction(restrictTo);
        }
    }, [restrictTo]);
    //LOGIC SET TO HIGHLIGHT
    useEffect(() => {
        if (highlight) {
            setHighlightActive(highlight);
        }
    }, [highlight]);
    const value = useMemo(() => ({
        application,
        onSubmit,
        onNavigate: externalNavigate,
        isLoading,
        filtersOpen,
        setFiltersOpen,
        defaultFilters,
        searchActive,
        setSearchActive,
        filterActive,
        setFilterActive,
        formInitialized,
        restriction,
        setRestriction,
        highlightActive,
        onResetValues,
        resetValues,
        handleResetDefaultFilters,
        handleCheckIfEqualToDefault,
        makeParams,
    }), [
        results,
        application,
        externalSubmit,
        externalNavigate,
        filtersOpen,
        setFiltersOpen,
        isLoading,
        defaultFilters,
        searchActive,
        setSearchActive,
        filterActive,
        setFilterActive,
        formInitialized,
        restriction,
        setRestriction,
        highlightActive,
        onResetValues,
        resetValues,
        handleResetDefaultFilters,
        handleCheckIfEqualToDefault,
        makeParams,
    ]);
    ///////////////////////////////////////////////
    // RENDER
    ///////////////////////////////////////////////
    return (_jsx(CustomFormContext.Provider, { value: { results, ...value }, children: _jsx(FormProvider, { ...methods, children: children }) }));
};
//# sourceMappingURL=SourSearchWrapper.js.map